import { tKey } from 'ember-intl';
import type { ChangeDialect } from 'lumen/utils/activity-changes';

export const ACTION_STATUSES = ['create', 'update', 'delete'] as const;

export type ActionStatus = (typeof ACTION_STATUSES)[number];

export const ACTION_STATUS_TRANSLATION_KEYS: Record<ActionStatus, string> = {
  create: tKey('settings.activity_history.action_status.create'),
  update: tKey('settings.activity_history.action_status.update'),
  delete: tKey('settings.activity_history.action_status.delete'),
} as const;

export const ACTIVITY_ACTION_TRANSLATION_KEYS = {
  password_reset_sent: tKey(
    'settings.activity_history.action.password_reset_sent',
  ),
  nonexisting_password_reset: tKey(
    'settings.activity_history.action.nonexisting_password_reset',
  ),
  inactive_password_reset: tKey(
    'settings.activity_history.action.inactive_password_reset',
  ),
  pw_reset_form_invalid: tKey(
    'settings.activity_history.action.pw_reset_form_invalid',
  ),
  pw_reset_form_valid: tKey(
    'settings.activity_history.action.pw_reset_form_valid',
  ),
  login_inactive: tKey('settings.activity_history.action.login_inactive'),
  login_no_hotels: tKey('settings.activity_history.action.login_no_hotels'),
  login_twofactor: tKey('settings.activity_history.action.login_twofactor'),
  login_twofactor_email: tKey(
    'settings.activity_history.action.login_twofactor_email',
  ),
  login_invalid_loginoption: tKey(
    'settings.activity_history.action.login_invalid_loginoption',
  ),
  login_migrated: tKey('settings.activity_history.action.login_migrated'),
  login_invalid: tKey('settings.activity_history.action.login_invalid'),
  login_success: tKey('settings.activity_history.action.login_success'),
  mfa_email_sent: tKey('settings.activity_history.action.mfa_email_sent'),
  email_updated: tKey('settings.activity_history.action.email_updated'),
  bulkpush_create: tKey('settings.activity_history.action.bulkpush_create'),
  recommendation_action_create: tKey(
    'settings.activity_history.action.recommendation_action_create',
  ),
  restriction_recommendation_action_create: tKey(
    'settings.activity_history.action.restriction_recommendation_action_create',
  ),
  accept_sso: tKey('settings.activity_history.action.accept_sso'),
  compsetrelation_rms_create: tKey(
    'settings.activity_history.action.compsetrelation_rms_create',
  ),
  compsetrelation_rms_delete: tKey(
    'settings.activity_history.action.compsetrelation_rms_delete',
  ),
  compsetrelation_rms_update: tKey(
    'settings.activity_history.action.compsetrelation_rms_update',
  ),
  compsetrelation_app_primary_create: tKey(
    'settings.activity_history.action.compsetrelation_app_primary_create',
  ),
  compsetrelation_app_primary_delete: tKey(
    'settings.activity_history.action.compsetrelation_app_primary_delete',
  ),
  compsetrelation_app_primary_update: tKey(
    'settings.activity_history.action.compsetrelation_app_primary_update',
  ),
  compsetrelation_app_secondary_create: tKey(
    'settings.activity_history.action.compsetrelation_app_secondary_create',
  ),
  compsetrelation_app_secondary_delete: tKey(
    'settings.activity_history.action.compsetrelation_app_secondary_delete',
  ),
  compsetrelation_app_secondary_update: tKey(
    'settings.activity_history.action.compsetrelation_app_secondary_update',
  ),
  user_updated: tKey('settings.activity_history.action.user_updated'),
  user_update_name: tKey('settings.activity_history.action.user_update_name'),
  user_update_language: tKey(
    'settings.activity_history.action.user_update_language',
  ),
  keydate_create: tKey('settings.activity_history.action.keydate_create'),
  keydate_update: tKey('settings.activity_history.action.keydate_update'),
  keydate_delete: tKey('settings.activity_history.action.keydate_delete'),
  keydatenote_create: tKey(
    'settings.activity_history.action.keydatenote_create',
  ),
  keydatenote_update: tKey(
    'settings.activity_history.action.keydatenote_update',
  ),
  keydatenote_delete: tKey(
    'settings.activity_history.action.keydatenote_delete',
  ),
  extrahotelinfo_update_pms: tKey(
    'settings.activity_history.action.extrahotelinfo_update_pms',
  ),
  extrahotelinfo_update_rms: tKey(
    'settings.activity_history.action.extrahotelinfo_update_rms',
  ),
  extrahotelinfo_update_channel_mgr: tKey(
    'settings.activity_history.action.extrahotelinfo_update_channel_mgr',
  ),
  roomtypecompetitormapping_create: tKey(
    'settings.activity_history.action.roomtypecompetitormapping_create',
  ),
  roomtypecompetitormapping_update: tKey(
    'settings.activity_history.action.roomtypecompetitormapping_update',
  ),
  roomtypecompetitormapping_delete: tKey(
    'settings.activity_history.action.roomtypecompetitormapping_delete',
  ),
  reportconfig_create: tKey(
    'settings.activity_history.action.reportconfig_create',
  ),
  reportconfig_update: tKey(
    'settings.activity_history.action.reportconfig_update',
  ),
  reportconfig_delete: tKey(
    'settings.activity_history.action.reportconfig_delete',
  ),
  subscription_portfolio_linked: tKey(
    'settings.activity_history.action.subscription_portfolio_linked',
  ),
  subscription_portfolio_unlinked: tKey(
    'settings.activity_history.action.subscription_portfolio_unlinked',
  ),
  subscription_portfolio_changed: tKey(
    'settings.activity_history.action.subscription_portfolio_changed',
  ),
  central_mapping_segmentation_view_market_segment: tKey(
    'settings.activity_history.action.central_mapping_segmentation_view_market_segment',
  ),
  central_mapping_segmentation_view_rate_code: tKey(
    'settings.activity_history.action.central_mapping_segmentation_view_rate_code',
  ),
  central_mapping_fallback_enabled: tKey(
    'settings.activity_history.action.central_mapping_fallback_enabled',
  ),
  central_mapping_fallback_disabled: tKey(
    'settings.activity_history.action.central_mapping_fallback_disabled',
  ),
  central_mapping_permission_delegated: tKey(
    'settings.activity_history.action.central_mapping_permission_delegated',
  ),
  central_mapping_permission_portfolio_only: tKey(
    'settings.activity_history.action.central_mapping_permission_portfolio_only',
  ),
  central_mapping_settings_updated: tKey(
    'settings.activity_history.action.central_mapping_settings_updated',
  ),
  central_mapping_portfolio_changed: tKey(
    'settings.activity_history.action.central_mapping_portfolio_changed',
  ),
  central_mapping_relation_changed_itemized: tKey(
    'settings.activity_history.action.central_mapping_relation_changed_itemized',
  ),
  central_mapping_relation_changed_count: tKey(
    'settings.activity_history.action.central_mapping_relation_changed_count',
  ),
  central_mapping_category_changed_itemized: tKey(
    'settings.activity_history.action.central_mapping_category_changed_itemized',
  ),
  central_mapping_category_changed_count: tKey(
    'settings.activity_history.action.central_mapping_category_changed_count',
  ),
} as const;

export type ActivityActionTranslationKeys =
  keyof typeof ACTIVITY_ACTION_TRANSLATION_KEYS;

// Two backend mechanisms describe the same kind of change ("this field went
// from X to Y") in different shapes, each with its own field/value
// vocabulary. Bundling those vocabularies as dialects lets the rendering
// code stay one implementation parameterized by dialect (see
// `lumen/utils/activity-changes`), instead of one code path per mechanism.

/**
 * `changes.mapping_changes[]`, built explicitly for batched central mapping
 * edits (PIN-1351). Values are real JSON types (booleans, `{id, name}`
 * references).
 *
 * `commissionable` reuses the exact option ids and translation keys from the
 * Central Mapping "Commissionable" dropdown
 * (dropdown-commissionable.gts / CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID),
 * so the audit trail always matches the setting.
 */
export const MAPPING_CHANGES_DIALECT: ChangeDialect = {
  name: 'mapping-changes',
  fieldLabels: {
    bar_based: tKey('settings.central_mapping.mapping.bar_based'),
    category: tKey('general.category'),
    category_added: tKey(
      'settings.activity_history.expanded.field_category_added',
    ),
    category_renamed: tKey(
      'settings.activity_history.expanded.field_category_renamed',
    ),
    commissionable: tKey('settings.central_mapping.mapping.commissionable'),
  },
  sharedValueLabels: {},
  valueLabelsByField: {
    commissionable: {
      gross: tKey('settings.central_mapping.gross'),
      net: tKey('settings.central_mapping.net'),
      no: tKey('general.no'),
      // `all` and `unmapped` (the other two CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID
      // values) never appear in change history, so they have no entry here.
    },
  },
};

/**
 * `changes[field] = [oldValue, newValue]`, produced by the backend's
 * automatic audit-log registration (PIN-1339) for any category. Field names
 * arrive as raw identifiers because PIN-1351 never speced this shape.
 *
 * Booleans arrive as the strings "True"/"False" (Python's `str(bool)`), not
 * JSON booleans, hence the string entries below.
 *
 * `allow_hotel_admin_and_editors_to_edit_mapping` overrides the shared
 * Yes/No labels because its two states have real product wording (the
 * "Mapping permissions" modal's radio options) that Yes/No would lose.
 */
export const FIELD_TUPLE_DIALECT: ChangeDialect = {
  name: 'field-tuple',
  fieldLabels: {
    allow_hotel_admin_and_editors_to_edit_mapping: tKey(
      'settings.activity_history.expanded.field_mapping_permission',
    ),
    mapping_segmentation_view: tKey(
      'settings.activity_history.expanded.field_mapping_segmentation_view',
    ),
    rate_plan_fallback_enabled: tKey(
      'settings.activity_history.expanded.field_rate_plan_fallback_enabled',
    ),
  },
  sharedValueLabels: {
    False: tKey('general.no'),
    True: tKey('general.yes'),
    market_segment: tKey(
      'settings.activity_history.expanded.value_market_segment',
    ),
    rate_code: tKey('settings.activity_history.expanded.value_rate_code'),
  },
  valueLabelsByField: {
    allow_hotel_admin_and_editors_to_edit_mapping: {
      False: tKey(
        'settings.activity_history.expanded.value_mapping_permission_restricted',
      ),
      True: tKey(
        'settings.activity_history.expanded.value_mapping_permission_allowed',
      ),
    },
  },
};

export const ACTIVITY_CATEGORY_TRANSLATION_KEYS = {
  account: tKey('settings.activity_history.category.account'),
  // "Central mapping" is internal jargon; customers just see "Mapping".
  central_mapping: tKey('general.mapping'),
  competitors: tKey('settings.activity_history.category.competitors'),
  hotel: tKey('settings.activity_history.category.hotel'),
  reports: tKey('settings.activity_history.category.reports'),
  users: tKey('settings.activity_history.category.users'),
  pricing_strategy: tKey('settings.activity_history.category.pricing_strategy'),
} as const;

export type ActivityCategory = keyof typeof ACTIVITY_CATEGORY_TRANSLATION_KEYS;
